import { zodResolver } from '@hookform/resolvers/zod';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import SaveIcon from '@mui/icons-material/Save';
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';

import { BadRequestError } from '_lib/api/apiErrors';
import { Note, NoteCreateDTO, NoteCreateSchema, TimezoneAbbreviationEnum } from '_lib/api/apiTypes.shim';
import { NoteChildResource } from '_lib/api/stApiResourceFactory';

import { Card } from '_lib/components/Card/Card';
import { CardContent } from '_lib/components/CardContent/CardContent';
import { CircularProgress } from '_lib/components/CircularProgress/CircularProgress';
import { ControlHeader } from '_lib/components/ControlHeader/ControlHeader';
import { Form } from '_lib/components/Form/Form';
import { IconButton } from '_lib/components/IconButton/IconButton';
import { TextFieldFormItem } from '_lib/components/TextFieldFormItem/TextFieldFormItem';
import { Tooltip } from '_lib/components/Tooltip/Tooltip';
import { NoteList } from '_lib/featureComponents/NoteList/NoteList';
import { apiSubmitHandler } from '_lib/utils/hookFormUtils';
import { reportException } from '_lib/utils/sentryService';

import type { ApiSubmitHandler } from '_lib/types/hookForm.types';

interface NoteFormProps {
  notesResource: NoteChildResource;
  onNoteCreated: (note: Note) => void;
  onCancel: () => void;
}

const NoteForm = (props: NoteFormProps): React.ReactElement => {
  const { notesResource, onNoteCreated, onCancel } = props;

  const [isSaving, setIsSaving] = useState(false);

  const formMethods = useForm<NoteCreateDTO>({
    resolver: zodResolver(NoteCreateSchema),
    defaultValues: { text: '', isPinned: false },
    mode: 'onBlur'
  });

  const { handleSubmit, setError, control } = formMethods;

  const handleAddNote: ApiSubmitHandler<NoteCreateDTO> = async (formNote, { setFormErrors }) => {
    try {
      setIsSaving(true);

      // Map the form to a new note creation object. Force isPinned to false.
      const noteBeingAdded: NoteCreateDTO = {
        ...formNote,
        isPinned: false
      };

      const noteCreated = await notesResource.create(noteBeingAdded);
      onNoteCreated(noteCreated);
    } catch (err) {
      if (err instanceof BadRequestError) {
        setFormErrors(err);
      }
      reportException(err);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Card variant='outlined'>
      <ControlHeader
        header='Add Note'
        controls={
          <>
            <Tooltip title='Save' placement='top'>
              <IconButton type='submit' form='note-form' key={'note-form-submit'}>
                <SaveIcon />
                {isSaving && <CircularProgress sx={{ position: 'absolute', zIndex: 1 }} />}
              </IconButton>
            </Tooltip>
            <Tooltip title='Cancel' placement='top'>
              <IconButton onClick={() => onCancel()}>
                <CloseIcon />
              </IconButton>
            </Tooltip>
          </>
        }
      />
      <CardContent>
        <Form id='note-form' onSubmit={apiSubmitHandler(handleSubmit, setError, handleAddNote)}>
          <TextFieldFormItem
            multiline={true}
            minRows={3}
            maxRows={10}
            name={'text'}
            label='Note'
            control={control}
            required
          />
        </Form>
      </CardContent>
    </Card>
  );
};

interface NotesCardProps {
  notes: Note[];
  notesResource: NoteChildResource;
  timezone: TimezoneAbbreviationEnum;
  onNotesUpdated: (notes: Note[]) => void;
}

export const NotesCard = (props: NotesCardProps): React.ReactElement => {
  const { notes, notesResource, timezone, onNotesUpdated } = props;

  const sortedNotes: Note[] = notes.slice().sort((a: Note, b: Note) => {
    if (a.isPinned && !b.isPinned) return -1;
    if (b.isPinned && !a.isPinned) return 1;
    if (a.modifiedAt < b.modifiedAt) return 1;
    if (a.modifiedAt > b.modifiedAt) return -1;
    return 0;
  });

  const [isAddingNote, setIsAddingNote] = useState(false);

  const renderNoteList = (): React.ReactElement => {
    return (
      <Card variant='outlined'>
        <ControlHeader
          header='Notes'
          controls={
            <Tooltip title='Add' placement='top'>
              <IconButton onClick={() => setIsAddingNote(true)}>
                <AddIcon />
              </IconButton>
            </Tooltip>
          }
        />
        <NoteList notes={sortedNotes} timezone={timezone} />
      </Card>
    );
  };

  return isAddingNote ? (
    <NoteForm
      notesResource={notesResource}
      onNoteCreated={(newNote) => {
        onNotesUpdated([...sortedNotes, newNote]);
        setIsAddingNote(false);
      }}
      onCancel={() => setIsAddingNote(false)}
    />
  ) : (
    renderNoteList()
  );
};
